import { createContext, useContext, type CSSProperties, type ReactNode } from "react";

import { cx } from "@/lib/cx";
import type { EnterProps } from "@/ui/motion";
import { DisclosureChevron, ROW_HEIGHT, SEPARATOR_INSET, type ListDensity } from "./atoms";

/**
 * Density, inherited.
 *
 * `GroupedList` used to take a `density` and write it to a `data-grouped-density`
 * attribute that nothing read, while `GroupedRow` defaulted to `comfortable` on
 * its own — so `<GroupedList density="compact">` silently produced comfortable rows
 * unless the caller *also* threaded the density through every `ListSection` and
 * every row by hand. Two features did exactly that; one of them wrote it up as a
 * bug.
 *
 * Context rather than prop-drilling because the three components are always
 * nested and a row should not have to be told twice. An explicit prop on a
 * section or a row still wins over what it inherits, which is what a mixed group
 * (compact records, one comfortable action row) needs.
 *
 * Deliberately `undefined` at the root rather than `"comfortable"`: `undefined`
 * means *nobody has said*, which is what decides whether the `data-density`
 * attribute — the CSS hook that drives `min-h-row` for `ListRow`, `Skeleton` and
 * the workout rows — is emitted at all. Emitting it unasked would move every
 * row-height inside a grouped region from the root's 40px recipe.
 */
const DensityContext = createContext<ListDensity | undefined>(undefined);

/**
 * The grouped inset list — the most recognisable structure in iOS, and the one
 * thing this app had no equivalent of.
 *
 * ```
 * GroupedList              bg-grouped                the page behind the groups
 *   ListSection
 *     header               text-caption uppercase, inset 16px
 *     group card           bg-grouped-surface  rounded-surface  squircle  clipped
 *       GroupedRow         40/44px, px-inset, separator inset to the TEXT
 *       GroupedRow
 *     footnote             text-caption text-subtle, inset 16px
 *   ListSection            12px below
 * ```
 *
 * Three rules are baked in because each of them is a measured failure if it is
 * got wrong (`IOS-DESIGN.md` §4.1):
 *
 * 1. **`bg-grouped` and `bg-grouped-surface` are a pair.** A white group on the
 *    ordinary page measures 1.02:1 against it and disappears. `GroupedList`
 *    paints the page colour itself so a caller cannot use one without the
 *    other, and so a grouped region can sit inside an otherwise ordinary
 *    screen.
 * 2. **The separator starts at the text**, not at the card edge — that is
 *    `row-separator`, driven by `--busee-separator-inset`, which the section
 *    sets once for every row inside it. `divide-y` cannot express this.
 * 3. **Corners belong to the group, not to the row.** Rows are square and the
 *    group clips them; rounding the first and last row individually produces a
 *    1px seam the moment a row has a background of its own (hover, selection).
 *
 * Density: 40px rows. This is *settings-shaped* content — a dashboard card, a
 * peek's field list, an account menu. It is not the 114-card board and it is
 * not the task list; those keep `UI-REVIEW.md` §6's denser targets, which is
 * what `density="compact"` (32px) is for when a grouped list does hold records.
 */

/* -------------------------------------------------------------------------- */

export type GroupedListProps = {
  children: ReactNode;
  /** Attribute-only arrival treatment for the group already in the layout. */
  enter?: EnterProps;
  /**
   * Applies to every section and every row inside that does not set its own.
   * Left unset, rows are `comfortable` (40px) and `min-h-row` keeps whatever it
   * inherits from the page.
   */
  density?: ListDensity;
  /**
   * Run the grouped background to the edge of the scroll column on a phone.
   *
   * The shell insets its content by `--spacing-pad`, and an iOS grouped list is
   * edge-to-edge, so the region cancels that padding at small widths and
   * becomes a rounded region of its own once there is room around it.
   */
  bleed?: boolean;
  as?: "div" | "section";
  className?: string;
};

export function GroupedList({
  children,
  enter,
  density,
  bleed = true,
  as: Element = "div",
  className,
}: GroupedListProps) {
  return (
    <DensityContext.Provider value={density}>
      <Element
        {...enter}
        // The CSS hook for `min-h-row`, emitted only when a density was
        // actually asked for — see {@link DensityContext}.
        data-density={density}
        className={cx(
          "flex flex-col gap-4 bg-grouped py-4 text-ink",
          bleed
            // Cancels `--busee-page-inset`, never a fixed rung. The shell's
            // gutter is 12px on a phone and 16px from `sm` up; `-mx-pad` was a
            // hard-coded 16 and, once the compact pass moved the mobile gutter
            // to 12, over-bled the region by 4px on each side and gave every
            // screen holding a grouped list a horizontal scrollbar.
            ? "mx-[calc(var(--busee-page-inset,0px)*-1)] px-inset sm:mx-0 sm:rounded-surface sm:px-inset"
            : "rounded-surface px-inset",
          className,
        )}
      >
        {children}
      </Element>
    </DensityContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */

export type ListSectionProps = {
  /** The uppercase eyebrow above the group. */
  header?: ReactNode;
  /** A trailing control on the header line — a count, an "Edit". */
  headerAction?: ReactNode;
  /** The explanatory line under the group. Footnotes go here, not in a row. */
  footnote?: ReactNode;
  /**
   * Where every separator inside this group starts, in px.
   *
   * Defaults to 52 (16px group padding + a 24px leading slot + its 12px gap)
   * when the rows have a leading slot, and 16 otherwise. Pass a number only
   * when a row's leading content is an unusual width.
   */
  inset?: number;
  density?: ListDensity;
  /**
   * `ul` when the rows are a list of records — `GroupedRow as="li"` then. `div`
   * is right for a settings group, where each row is its own thing.
   */
  as?: "div" | "ul";
  children: ReactNode;
  className?: string;
  /** Classes on the group card itself, not on the section. */
  groupClassName?: string;
};

export function ListSection({
  header,
  headerAction,
  footnote,
  inset = SEPARATOR_INSET.plain,
  density,
  as: Element = "div",
  children,
  className,
  groupClassName,
}: ListSectionProps) {
  const inherited = useContext(DensityContext);
  const resolved = density ?? inherited;

  return (
    <section className={cx("flex flex-col", className)}>
      {header || headerAction ? (
        <div className="flex items-end justify-between gap-3 px-1 pb-2">
          {header ? (
            <h2 className="busee-group-head min-w-0 truncate text-section font-normal text-ps1-gold">{header}</h2>
          ) : (
            <span />
          )}
          {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
        </div>
      ) : null}

      <Element
        data-density={resolved}
        // Set once here so a row never has to know its own gutter, and so a
        // group of mixed rows still lines its separators up with each other.
        style={{ "--busee-separator-inset": `${inset}px` } as CSSProperties}
        className={cx(
          "busee-group overflow-hidden rounded-surface border-2 border-ps1-orange bg-ps1-panel",
          groupClassName,
        )}
      >
        <DensityContext.Provider value={resolved}>{children}</DensityContext.Provider>
      </Element>

      {footnote ? (
        <p className="px-1 pt-2 text-label text-subtle">{footnote}</p>
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * What kind of thing the row is, which is the only thing that colours its title.
 *
 * - `default` — a setting, a value, a destination. Ink.
 * - `action` — an *additive or navigational* row: `＋ Add exercise`,
 *   `Show 22 more`, `Reload from routine`. iOS tints these with the accent, and
 *   the tint is what separates "this row does something" from "this row reports
 *   something" in a list where both are 40px of text. `accent-ink`, never
 *   `accent`: a row title is 15px, and `IOS-DESIGN.md` §1.1 makes `text-accent`
 *   illegal below 24px regular / 19px semibold.
 * - `destructive` — Delete, Sign out. Red title, and it goes last in its group.
 *
 * An axis rather than a `destructive` boolean because the third case appeared
 * the moment the surfaces were built: four call sites across two features had
 * each spelled `title={<span className="text-accent-ink">…</span>}`, which is
 * one decision written four times and no way to grep for "the action rows".
 */
export type GroupedRowTone = "default" | "action" | "destructive";

const ROW_TONES: Record<GroupedRowTone, string> = {
  default: "text-ink",
  action: "text-accent-ink",
  destructive: "text-danger",
};

export type GroupedRowProps = {
  /** The row's main line. */
  title: ReactNode;
  /** A quiet second line under it. */
  subtitle?: ReactNode;
  /**
   * The leading slot: an icon, a tick, a lane dot. 24px wide by default and it
   * grows to fit anything larger, so a 28px medallion does not knock the title
   * out of alignment. When present, pass `inset={SEPARATOR_INSET.leading}` on
   * the section so the separators agree — or the slot's real width, if it is
   * not 24.
   */
  leading?: ReactNode;
  /** The right-aligned value — `text-label text-muted`. */
  value?: ReactNode;
  /** Anything else trailing: a badge, a switch. Sits after the value. */
  trailing?: ReactNode;
  /**
   * Makes the whole row a button and, by default, draws the chevron. A row
   * with no `onSelect` and no chevron does not open anything.
   */
  onSelect?: () => void;
  /**
   * Render the row's interactive body as something other than a `<button>`,
   * keeping every bit of this row's geometry: the inset, the gap, the hover and
   * active fills, the inward focus offset, the separator and the chevron.
   *
   * This exists because **a row that navigates must not be a button.** The
   * dashboard is a list of destinations, and a button has no middle-click, no
   * "open in new tab", no status-bar preview of where it goes, and announces
   * itself as "button" to a screen reader. `ui/` does not know about the router
   * — deliberately — so the caller supplies the element and this supplies
   * everything else:
   *
   * ```tsx
   * <GroupedRow as="li" render={(props) => <Link to={to} {...props} />} … />
   * ```
   *
   * Takes precedence over `onSelect`, and implies the chevron the same way.
   */
  render?: (props: { className: string; children: ReactNode }) => ReactNode;
  /** Overrides the chevron: force it off on a row that only toggles. */
  chevron?: boolean;
  /** This row's detail is the one currently open — rotates the chevron. */
  expanded?: boolean;
  /** Draws the rail and bumps the title's weight. Never a tint (P6). */
  selected?: boolean;
  /** What kind of row this is. See {@link GroupedRowTone}. */
  tone?: GroupedRowTone;
  disabled?: boolean;
  density?: ListDensity;
  /** Per-row override of the section's separator inset, in px. */
  inset?: number;
  as?: "div" | "li";
  className?: string;
};

export function GroupedRow({
  title,
  subtitle,
  leading,
  value,
  trailing,
  onSelect,
  render,
  chevron,
  expanded = false,
  selected = false,
  tone = "default",
  disabled = false,
  density,
  inset,
  as: Element = "div",
  className,
}: GroupedRowProps) {
  const inherited = useContext(DensityContext);
  const resolved = density ?? inherited ?? "comfortable";
  const showChevron = chevron ?? Boolean(onSelect || render);

  /**
   * The interactive body's skin, shared by the button and by whatever `render`
   * supplies — so a link row and a button row cannot drift into two different
   * hover fills or two different focus offsets.
   *
   * Rows are square: the group clips the corners, so a hover fill that rounded
   * here would leave a seam on the first and last row. The focus offset points
   * *inward* for the same reason — an outset ring is clipped by the card.
   */
  const interactive = cx(
    "flex min-w-0 flex-1 items-stretch gap-3 px-inset text-left",
    "transition-colors dur-fast hover:bg-hover active:bg-active",
    "disabled:pointer-events-none",
    "focus-inset",
  );

  const body = (
    <>
      {leading ? (
        // `min-w-6`, not `w-6`: the slot is 24px for a glyph and grows for a
        // 28px medallion rather than letting it overflow and shift the title.
        <span className="flex min-w-6 shrink-0 items-center justify-center self-center">
          {leading}
        </span>
      ) : null}

      <span className="flex min-w-0 flex-1 flex-col justify-center gap-1 py-2 text-left">
        <span className={cx("title-clamp text-body", selected && "font-semibold", ROW_TONES[tone])}>
          {title}
        </span>
        {subtitle ? <span className="truncate text-caption text-muted">{subtitle}</span> : null}
      </span>

      {value ? (
        <span className="shrink-0 self-center truncate text-label text-muted">{value}</span>
      ) : null}
      {trailing ? (
        <span className="flex shrink-0 items-center gap-2 self-center">{trailing}</span>
      ) : null}
      {showChevron ? <DisclosureChevron expanded={expanded} className="self-center" /> : null}
    </>
  );

  return (
    <Element
      data-selected={selected || undefined}
      style={inset === undefined ? undefined : ({ "--busee-separator-inset": `${inset}px` } as CSSProperties)}
      className={cx(
        // `row-separator` draws on `::after` and removes itself on the last row;
        // `rail-selected` draws on `::before`. They are deliberately different
        // pseudo-elements so a selected row can carry both.
        "group/row relative flex items-stretch row-separator",
        ROW_HEIGHT[resolved],
        selected && "rail-selected",
        disabled && "opacity-45",
        className,
      )}
    >
      {render ? (
        render({ className: interactive, children: body })
      ) : onSelect ? (
        <button type="button" onClick={onSelect} disabled={disabled} className={interactive}>
          {body}
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-stretch gap-3 px-inset">{body}</span>
      )}
    </Element>
  );
}
